// File: OwnerDashboard.js

import React, { useEffect, useState, useCallback } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import CashOffersPanel from "./CashOffersPanel";
import "./OwnerDashboard.css";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";

import { getTradeImageSrc, handleImgError } from "../utils/images";

/* ---------------- Helpers ---------------- */

const moneyZAR = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "—";
  return num.toLocaleString("en-ZA", { style: "currency", currency: "ZAR" });
};

const formatLocation = (loc) => {
  if (!loc) return "—";
  if (typeof loc === "string") return loc;
  const parts = [loc.city, loc.region, loc.country].filter(Boolean);
  return parts.join(", ") || "—";
};

const norm = (v) => String(v ?? "").trim();
const lower = (v) => norm(v).toLowerCase();

const stageToPretty = (s) => {
  const k = lower(s);
  const MAP = {
    seller_prep: "Seller Prep",
    buyer_approval: "Buyer Approval",
    awaiting_payment: "Awaiting Payment",
    ready_for_inspection: "Ready for Inspection",
    awaiting_dropoff: "Awaiting Drop-off",
    inspection_passed: "Inspection Passed",
    pickup_scheduled: "Pickup Scheduled",
    pickup_in_progress: "Pickup In Progress",
    hub_inspection_pass: "Hub Inspection Passed",
    delivery_scheduled: "Delivery Scheduled",
    delivery_in_progress: "Delivery In Progress",
    delivered_pending_buyer_verification: "Awaiting Verification",
    verification_passed: "Buyer Verified",
    completed: "Completed",
    cancelled: "Cancelled",
  };
  return MAP[k] || (k ? k.replace(/_/g, " ") : "—");
};

const compactTradeStatus = (statusRaw) => {
  const s = lower(statusRaw);
  const MAP = {
    open: "Open",
    active: "Active",
    pending: "Pending",
    seller_prep: "Prep",
    buyer_approval: "Review",
    awaiting_payment: "Payment",
    ready_for_inspection: "Inspection",
    awaiting_dropoff: "Drop-off",
    inspection_passed: "Passed",
    pickup_scheduled: "Pickup",
    pickup_in_progress: "Pickup",
    hub_inspection_pass: "Hub Pass",
    delivery_scheduled: "Delivery",
    delivery_in_progress: "Delivery",
    delivered_pending_buyer_verification: "Awaiting Verify",
    verification_passed: "Verified",
    completed: "Completed",
    closed_success: "Completed",
    cancelled: "Cancelled",
  };
  return MAP[s] || (s ? s.replace(/_/g, " ") : "Active");
};

const isCompletedStatus = (t) => ["completed", "closed_success"].includes(lower(t));

const getTradeImages = (trade, max = 6) => {
  const imgs = [];

  const push = (u) => {
    const url = norm(u);
    if (!url) return;
    if (imgs.includes(url)) return;
    imgs.push(url);
  };

  push(getTradeImageSrc(trade));

  const candidates = [
    trade?.thumbnailUrl,
    trade?.imageUrl,
    trade?.photoUrl,
    trade?.coverImage,
    trade?.images,
    trade?.photos,
    trade?.media,
    trade?.gallery,
    trade?.attachments,
  ];

  candidates.forEach((c) => {
    if (!c) return;
    if (typeof c === "string") push(c);
    else if (Array.isArray(c)) c.forEach((x) => push(x?.url || x?.src || x));
    else if (typeof c === "object") {
      Object.values(c).forEach((x) => push(x?.url || x?.src || x));
    }
  });

  return imgs.slice(0, max);
};

const getFulfillmentMethod = (wf) => lower(wf?.fulfillmentChoice?.method || "");
const getDropoffPartnerId = (wf) => lower(wf?.dropoff?.partnerId || "");
const getPaymentStatus = (wf) => lower(wf?.payment?.status || wf?.cash?.status || "");
const getInspectionFeeStatus = (wf) => lower(wf?.inspectionFee?.status || "");

const isPartnerDropoff = (wf) => {
  const stage = lower(wf?.stage || "");
  const method = getFulfillmentMethod(wf);
  const dropoffPartnerId = getDropoffPartnerId(wf);
  return (
    method === "partner_dropoff" ||
    dropoffPartnerId === "approved_dropoff_point" ||
    stage === "awaiting_dropoff"
  );
};

const stageToUi = (stageRaw, wf = null) => {
  const s = lower(stageRaw);
  const partnerDropoff = isPartnerDropoff(wf);
  const paymentStatus = getPaymentStatus(wf);
  const inspectionFeeStatus = getInspectionFeeStatus(wf);

  if (!s) {
    return {
      stageClass: "stage-seller_prep",
      ctaLabel: "View",
      ctaVariant: "secondary",
      progressPct: 15,
      routeHint: "",
    };
  }

  if (s === "seller_prep") {
    return {
      stageClass: "stage-seller_prep",
      ctaLabel: "View workflow",
      ctaVariant: "secondary",
      progressPct: 10,
      routeHint: "",
    };
  }

  if (s === "buyer_approval") {
    return {
      stageClass: "stage-buyer_approval",
      ctaLabel: "View workflow",
      ctaVariant: "secondary",
      progressPct: 20,
      routeHint: "",
    };
  }

  if (s === "awaiting_payment") {
    return {
      stageClass: "stage-awaiting_payment",
      ctaLabel: "View payment",
      ctaVariant: "primary",
      progressPct: 30,
      routeHint: "",
    };
  }

  if (s === "ready_for_inspection") {
    if (partnerDropoff) {
      return {
        stageClass: "stage-inspection",
        ctaLabel: "Choose route",
        ctaVariant: "primary",
        progressPct: 40,
        routeHint: "Awaiting seller route selection",
      };
    }

    if (paymentStatus === "paid" && inspectionFeeStatus !== "paid") {
      return {
        stageClass: "stage-inspection",
        ctaLabel: "Pay inspection fee",
        ctaVariant: "primary",
        progressPct: 45,
        routeHint: "Platform pickup",
      };
    }

    return {
      stageClass: "stage-inspection",
      ctaLabel: "View workflow",
      ctaVariant: "primary",
      progressPct: 50,
      routeHint: "Platform pickup",
    };
  }

  if (s === "awaiting_dropoff") {
    return {
      stageClass: "stage-awaiting_dropoff",
      ctaLabel: "View drop-off details",
      ctaVariant: "primary",
      progressPct: 55,
      routeHint: "Approved drop-off point",
    };
  }

  if (s === "inspection_passed") {
    return {
      stageClass: "stage-inspection_passed",
      ctaLabel: partnerDropoff ? "Track delivery prep" : "Track pickup",
      ctaVariant: "secondary",
      progressPct: 65,
      routeHint: partnerDropoff ? "Approved drop-off point" : "Platform pickup",
    };
  }

  if (s === "pickup_scheduled") {
    return {
      stageClass: "stage-pickup_scheduled",
      ctaLabel: "Track pickup",
      ctaVariant: "secondary",
      progressPct: 72,
      routeHint: "Platform pickup",
    };
  }

  if (s === "pickup_in_progress") {
    return {
      stageClass: "stage-pickup_in_progress",
      ctaLabel: "Track pickup",
      ctaVariant: "secondary",
      progressPct: 78,
      routeHint: "Platform pickup",
    };
  }

  if (s === "hub_inspection_pass") {
    return {
      stageClass: "stage-hub_inspection_pass",
      ctaLabel: "Track delivery prep",
      ctaVariant: "secondary",
      progressPct: 83,
      routeHint: "",
    };
  }

  if (s === "delivery_scheduled") {
    return {
      stageClass: "stage-delivery_scheduled",
      ctaLabel: "Track delivery",
      ctaVariant: "secondary",
      progressPct: 88,
      routeHint: "",
    };
  }

  if (s === "delivery_in_progress") {
    return {
      stageClass: "stage-delivery_in_progress",
      ctaLabel: "Track delivery",
      ctaVariant: "secondary",
      progressPct: 92,
      routeHint: "",
    };
  }

  if (s === "delivered_pending_buyer_verification") {
    return {
      stageClass: "stage-delivered_pending_buyer_verification",
      ctaLabel: "View status",
      ctaVariant: "secondary",
      progressPct: 96,
      routeHint: "Awaiting buyer verification",
    };
  }

  if (s === "verification_passed") {
    return {
      stageClass: "stage-verification_passed",
      ctaLabel: "View completion",
      ctaVariant: "secondary",
      progressPct: 99,
      routeHint: "",
    };
  }

  if (s === "completed") {
    return {
      stageClass: "stage-completed",
      ctaLabel: "View receipt",
      ctaVariant: "secondary",
      progressPct: 100,
      routeHint: "",
    };
  }

  if (s === "cancelled") {
    return {
      stageClass: "stage-cancelled",
      ctaLabel: "View",
      ctaVariant: "secondary",
      progressPct: 0,
      routeHint: "",
    };
  }

  return {
    stageClass: "stage-seller_prep",
    ctaLabel: "View",
    ctaVariant: "secondary",
    progressPct: 25,
    routeHint: "",
  };
};

/* ---------------- Component ---------------- */

const OwnerDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [myTrades, setMyTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [offersReceivedTotal, setOffersReceivedTotal] = useState(0);
  const [inProgressTotal, setInProgressTotal] = useState(0);
  const [completedTotal, setCompletedTotal] = useState(0);

  const [offerCountByTrade, setOfferCountByTrade] = useState({});
  const [wfByTrade, setWfByTrade] = useState({});

  const [activeIndexByTrade, setActiveIndexByTrade] = useState({});
  const [favoriteByTrade, setFavoriteByTrade] = useState({});

  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      setMyTrades([]);
      return;
    }

    setLoading(true);
    setError(null);

    const tradesRef = collection(db, "Trades");

    const qOwner = query(tradesRef, where("ownerId", "==", currentUser.uid));
    const qCreatedBy = query(tradesRef, where("createdBy", "==", currentUser.uid));
    const qSellerUid = query(tradesRef, where("sellerUid", "==", currentUser.uid));

    let latestOwner = [];
    let latestCreated = [];
    let latestSeller = [];

    const mergeAndSet = () => {
      const m = new Map();
      latestOwner.forEach((t) => m.set(t.id, t));
      latestCreated.forEach((t) => m.set(t.id, t));
      latestSeller.forEach((t) => m.set(t.id, t));

      const merged = Array.from(m.values()).sort((a, b) => {
        const aMs =
          (a.updatedAt?.toMillis?.() ?? a.updatedAt?.seconds * 1000) ||
          (a.createdAt?.toMillis?.() ?? a.createdAt?.seconds * 1000) ||
          0;
        const bMs =
          (b.updatedAt?.toMillis?.() ?? b.updatedAt?.seconds * 1000) ||
          (b.createdAt?.toMillis?.() ?? b.createdAt?.seconds * 1000) ||
          0;
        return bMs - aMs;
      });

      setMyTrades(merged);
      setLoading(false);
    };

    const unsub1 = onSnapshot(
      qOwner,
      (snap) => {
        latestOwner = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        mergeAndSet();
      },
      (e) => {
        console.error("❌ Failed to load owner trades (ownerId):", e);
        setError("Failed to load your trades. Please try again.");
        setLoading(false);
      }
    );

    const unsub2 = onSnapshot(
      qCreatedBy,
      (snap) => {
        latestCreated = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        mergeAndSet();
      },
      (e) => {
        console.error("❌ Failed to load owner trades (createdBy):", e);
      }
    );

    const unsub3 = onSnapshot(
      qSellerUid,
      (snap) => {
        latestSeller = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        mergeAndSet();
      },
      (e) => {
        console.error("❌ Failed to load owner trades (sellerUid):", e);
      }
    );

    return () => {
      try { unsub1(); } catch {}
      try { unsub2(); } catch {}
      try { unsub3(); } catch {}
    };
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!showConfirmation) return;
    const t = setTimeout(() => setShowConfirmation(false), 4000);
    return () => clearTimeout(t);
  }, [showConfirmation]);

  useEffect(() => {
    const handler = (e) => {
      if (e?.detail === true) {
        setShowConfirmation(true);
        toast.success("✅ Offer successfully sent.");
      }
    };
    window.addEventListener("cash-offer-sent", handler);
    window.addEventListener("trade-offer-sent", handler);
    return () => {
      window.removeEventListener("cash-offer-sent", handler);
      window.removeEventListener("trade-offer-sent", handler);
    };
  }, []);

  useEffect(() => {
    if (!currentUser?.uid) return;

    const offersRef = collection(db, "TradeOffers");
    const qByOwnerId = query(offersRef, where("tradeOwnerId", "==", currentUser.uid));
    const qBySellerUid = query(offersRef, where("sellerUid", "==", currentUser.uid));

    let latestA = [];
    let latestB = [];

    const recompute = () => {
      const m = new Map();
      latestA.forEach((o) => m.set(o.id, o));
      latestB.forEach((o) => m.set(o.id, o));
      const merged = Array.from(m.values());

      const byTrade = {};
      merged.forEach((o) => {
        const tradeId = norm(o.tradeId || o.meta?.tradeId || o.trade?.id);
        if (!tradeId) return;
        byTrade[tradeId] = (byTrade[tradeId] || 0) + 1;
      });

      setOfferCountByTrade(byTrade);
      setOffersReceivedTotal(merged.length);
    };

    const unsubA = onSnapshot(
      qByOwnerId,
      (snap) => {
        latestA = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        recompute();
      },
      (e) => console.warn("[OwnerDashboard] offers(tradeOwnerId) error:", e)
    );

    const unsubB = onSnapshot(
      qBySellerUid,
      (snap) => {
        latestB = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        recompute();
      },
      (e) => console.warn("[OwnerDashboard] offers(sellerUid) error:", e)
    );

    return () => {
      try { unsubA(); } catch {}
      try { unsubB(); } catch {}
    };
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return;

    const wfRef = collection(db, "TradeWorkflows");
    const qWfSeller = query(wfRef, where("sellerUid", "==", currentUser.uid));

    const unsub = onSnapshot(
      qWfSeller,
      (snap) => {
        const map = {};
        let inProg = 0;
        let done = 0;

        snap.forEach((d) => {
          const data = d.data() || {};
          const tradeId = norm(data.tradeId || data.tradeRefId || d.id);
          if (!tradeId) return;

          const stage = norm(data.stage || "");
          const partnerDropoff = isPartnerDropoff(data);
          const method = getFulfillmentMethod(data);

          map[tradeId] = {
            stage,
            stageLabel: stageToPretty(stage),
            fulfillmentMethod: method,
            isPartnerDropoff: partnerDropoff,
            isPlatformPickup: !partnerDropoff,
            paymentStatus: getPaymentStatus(data),
            inspectionFeeStatus: getInspectionFeeStatus(data),
            dropoff: data.dropoff || null,
          };

          const st = lower(stage);
          if (st === "completed") done++;
          else if (st && st !== "cancelled") inProg++;
        });

        setWfByTrade(map);
        setInProgressTotal(inProg);
        setCompletedTotal(done);
      },
      (e) => console.warn("[OwnerDashboard] workflows(sellerUid) error:", e)
    );

    return () => {
      try { unsub(); } catch {}
    };
  }, [currentUser?.uid]);

  const onCarouselScroll = useCallback((tradeId, el) => {
    if (!el) return;
    const idx = Math.round(el.scrollLeft / (el.clientWidth || 1));
    setActiveIndexByTrade((p) => (p[tradeId] === idx ? p : { ...p, [tradeId]: idx }));
  }, []);

  const onToggleFavorite = useCallback((tradeId) => {
    setFavoriteByTrade((p) => ({ ...p, [tradeId]: !p[tradeId] }));
  }, []);

  const onCtaClick = useCallback(
    (trade, stage, wf = null) => {
      const s = lower(stage);
      const partnerDropoff = isPartnerDropoff(wf);

      if (
        s === "awaiting_payment" ||
        s === "ready_for_inspection" ||
        s === "awaiting_dropoff" ||
        s.includes("inspection") ||
        s.includes("pickup") ||
        s.includes("delivery") ||
        s.includes("completed") ||
        partnerDropoff
      ) {
        navigate(`/trade/${trade.id}?tab=workflow`);
        return;
      }

      navigate(`/trade/${trade.id}`);
    },
    [navigate]
  );

  if (!currentUser) {
    return <p style={{ padding: 20 }}>Please log in to view your dashboard.</p>;
  }
  if (loading) {
    return <p style={{ padding: 20 }}>Loading your dashboard…</p>;
  }
  if (error) {
    return <p style={{ color: "red", padding: 20 }}>{error}</p>;
  }

  const postedTotal = myTrades.length;

  return (
    <div className="owner-dashboard-wrapper">
      <section className="owner-dashboard-hero" aria-label="Seller dashboard summary">
        <div className="owner-dashboard-hero__content">
          <span className="owner-eyebrow">Seller workspace</span>
          <h2 className="dashboard-heading">Your Dashboard</h2>
          <p className="owner-dashboard-hero__copy">
            Monitor your listings, cash offers, fulfilment progress and buyer actions from one clean workspace.
          </p>
        </div>

        <div className="owner-dashboard-hero__snapshot" aria-label="Dashboard snapshot">
          <div>
            <span>{postedTotal}</span>
            <small>Listings</small>
          </div>
          <div>
            <span>{offersReceivedTotal}</span>
            <small>Offers</small>
          </div>
          <div>
            <span>{inProgressTotal}</span>
            <small>In progress</small>
          </div>
        </div>
      </section>

      <div className="dashboard-section dashboard-section--kpis">
        <div className="owner-kpi-grid">
          <div className="owner-kpi-card">
            <div className="owner-kpi-card__label">Posted trades</div>
            <div className="owner-kpi-card__value">{postedTotal}</div>
          </div>

          <div className="owner-kpi-card">
            <div className="owner-kpi-card__label">Offers received</div>
            <div className="owner-kpi-card__value">{offersReceivedTotal}</div>
          </div>

          <div className="owner-kpi-card">
            <div className="owner-kpi-card__label">In progress</div>
            <div className="owner-kpi-card__value">{inProgressTotal}</div>
          </div>

          <div className="owner-kpi-card">
            <div className="owner-kpi-card__label">Completed</div>
            <div className="owner-kpi-card__value">
              {completedTotal || myTrades.filter((t) => isCompletedStatus(t.status)).length}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showConfirmation && (
          <motion.div
            className="confirmation-banner"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
          >
            ✅ Offer successfully sent.
          </motion.div>
        )}
      </AnimatePresence>

      <div className="dashboard-section">
        <h3 className="dashboard-subheading">Your Listed Trades</h3>

        {myTrades.length === 0 ? (
          <p className="empty-state">You haven’t posted any trades yet.</p>
        ) : (
          <ul className="trade-list">
            {myTrades.map((trade) => {
              const tradeId = trade.id;
              const wf = wfByTrade?.[tradeId] || null;
              const offers = offerCountByTrade?.[tradeId];

              const stageRaw = wf?.stage || trade.workflowStage || trade.wfStage || "";
              const prettyStage =
                wf?.stageLabel || stageToPretty(trade.workflowStage || trade.wfStage || "") || "—";
              const ui = stageToUi(stageRaw, wf);

              const images = getTradeImages(trade, 6);
              const slides = images.length ? images : [getTradeImageSrc(trade)];
              const activeIdx = activeIndexByTrade[tradeId] ?? 0;
              const isFav = !!favoriteByTrade[tradeId];

              const routeHint = ui.routeHint || "";
              const dropoffPoint =
                wf?.isPartnerDropoff && wf?.dropoff?.partnerName
                  ? wf.dropoff.partnerName
                  : wf?.isPartnerDropoff
                  ? "Approved Drop-off Point"
                  : "";

              const displayStatus = compactTradeStatus(trade.status || stageRaw || "active");

              return (
                <li key={trade.id} className="trade-card">
                  <div className="trade-card-media">
                    <span className="stage-badge">{prettyStage}</span>

                    <button
                      className={`trade-card-heart ${isFav ? "is-active" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onToggleFavorite(tradeId);
                      }}
                      aria-label={isFav ? "Unfavorite" : "Favorite"}
                      title={isFav ? "Unfavorite" : "Favorite"}
                      type="button"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 21s-7-4.6-9.5-8.4C.7 9.7 2.1 6.5 5.3 5.6c1.8-.5 3.6.1 4.7 1.4 1.1-1.3 2.9-1.9 4.7-1.4 3.2.9 4.6 4.1 2.8 7-2.5 3.8-9.5 8.4-9.5 8.4z" />
                      </svg>
                    </button>

                    <div
                      className="trade-card-carousel"
                      onScroll={(e) => onCarouselScroll(tradeId, e.currentTarget)}
                    >
                      {slides.map((src, idx) => (
                        <div className="trade-card-slide" key={`${tradeId}-img-${idx}`}>
                          <img
                            src={src}
                            alt={`${trade.title || "Trade"} image ${idx + 1}`}
                            onError={handleImgError}
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </div>

                    <div className="trade-card-dots" aria-hidden="true">
                      {slides.map((_, i) => (
                        <span
                          key={`${tradeId}-dot-${i}`}
                          className={`trade-card-dot ${activeIdx === i ? "is-active" : ""}`}
                        />
                      ))}
                    </div>

                    <div className="trade-card-progress">
                      <span style={{ width: `${ui.progressPct}%` }} />
                    </div>
                  </div>

                  <div className="trade-card-body trade-card-body--premium">
                    <div className="trade-card-topline">
                      <div className="trade-card-title-wrap">
                        <div className="trade-card-title">{trade.title || "Untitled trade"}</div>
                        <div className="trade-card-meta">
                          <span>{trade.categoryLabel || trade.category || "—"}</span>
                          <span className="dot">•</span>
                          <span>{formatLocation(trade.location)}</span>
                        </div>
                      </div>

                      <div className="trade-card-price">
                        {moneyZAR(trade.expectedCashAmount ?? trade.estimatedValue)}
                      </div>
                    </div>

                    <div className="trade-card-status-row">
                      <span className={`trade-chip ${ui.stageClass}`}>{prettyStage}</span>
                      {routeHint ? <span className="trade-chip trade-chip--soft">{routeHint}</span> : null}
                      {dropoffPoint ? <span className="trade-chip trade-chip--soft">{dropoffPoint}</span> : null}
                    </div>

                    <div className="trade-card-stats-grid">
                      <div className="trade-stat-box">
                        <div className="trade-stat-box__label">Views</div>
                        <div className="trade-stat-box__value">{trade.viewCount ?? 0}</div>
                      </div>

                      <div className="trade-stat-box">
                        <div className="trade-stat-box__label">Offers</div>
                        <div className="trade-stat-box__value">{offers ?? trade.offerCount ?? 0}</div>
                      </div>

                      <div className="trade-stat-box trade-stat-box--status">
                        <div className="trade-stat-box__label">Status</div>
                        <div className="trade-stat-box__value">{displayStatus}</div>
                      </div>
                    </div>

                    <div className="trade-card-footer">
                      <div className="trade-card-links">
                        <Link to={`/trade/${trade.id}`} className="dashboard-link">
                          View listing
                        </Link>

                        {wf ? (
                          <Link to={`/trade/${trade.id}?tab=workflow`} className="dashboard-link">
                            Track progress
                          </Link>
                        ) : null}

                        <Link to={`/notifications?tradeId=${trade.id}`} className="dashboard-link">
                          Notifications
                        </Link>
                      </div>

                      <div className="trade-card-cta">
                        <button
                          className={ui.ctaVariant === "secondary" ? "is-secondary" : ""}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onCtaClick(trade, stageRaw, wf);
                          }}
                          type="button"
                        >
                          {ui.ctaLabel}
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <section className="dashboard-section dashboard-section--cash">
        <div className="cash-offers-shell">
          <div className="cash-offers-hero">
            <div className="cash-offers-hero__left">
              <div className="cash-offers-icon" aria-hidden="true">R</div>

              <div>
                <span className="owner-eyebrow">Offer centre</span>
                <h3 className="cash-offers-title">Cash Offers Received</h3>
                <p className="cash-offers-copy">
                  Review buyer interest, compare offer activity and move the strongest cash deals forward.
                </p>
              </div>
            </div>

            <div className="cash-offers-summary" aria-label="Cash offer summary">
              <div className="cash-mini-stat">
                <span>{offersReceivedTotal}</span>
                <small>Total received</small>
              </div>
              <div className="cash-mini-stat">
                <span>{postedTotal}</span>
                <small>Listed trades</small>
              </div>
            </div>
          </div>

          <div className="cash-offers-panel cash-offers-panel--premium">
            <CashOffersPanel />
          </div>
        </div>
      </section>
    </div>
  );
};

export default OwnerDashboard;